import type { Notification } from '@/api/notifications';
import { OLD_FRONTEND_URL as OLD } from '@/lib/constants/api';

type LinkConfig = { link: string; label: string } | null;
type DescSegment = { text: string; bold: boolean };

export function getActionLink(
  n: Notification,
  t: (key: string) => string,
): LinkConfig {
  const {
    notificationTriggerId: trigger,
    resourceId,
    createdByCompanyId,
    payload: p,
    status,
  } = n;

  const map: Record<string, LinkConfig> = {
    'own-certificates-expiring': {
      link: `${OLD}/claims/${resourceId}/view`,
      label: t('actionLabels.update'),
    },
    'own-certificates-expired': {
      link: `${OLD}/claims/${resourceId}/view`,
      label: t('actionLabels.update'),
    },
    'claim-rejected': {
      link: `${OLD}/claims/${resourceId}/view`,
      label: t('actionLabels.review'),
    },
    'audit-requested': {
      link: `/audits/${resourceId}/view`,
      label: t('actionLabels.respond'),
    },
    'document-requested': {
      link: `${OLD}/company-documents/list`,
      label: t('actionLabels.respond'),
    },
    'assessment-requested': {
      link: `${OLD}/assessment-requests/${resourceId}/answer`,
      label:
        status === 'PENDING'
          ? t('actionLabels.begin')
          : t('actionLabels.continue'),
    },
    'assessment-commented': {
      link: p?.isAssessmentOwner
        ? `${OLD}/assessments/${resourceId}/review/${createdByCompanyId}`
        : `${OLD}/assessment-requests/${resourceId}/answer`,
      label: t('actionLabels.review'),
    },
    'assessment-requested-company-notify': {
      link: `${OLD}/assessment-requests/${resourceId}/answer`,
      label: t('actionLabels.continue'),
    },
    'assessment-question-answered': {
      link: `${OLD}/assessments/${resourceId}/review/${createdByCompanyId}`,
      label: t('actionLabels.review'),
    },
    'assessment-unarchived': {
      link: `${OLD}/assessment-requests/${resourceId}/answer`,
      label: t('actionLabels.continue'),
    },
    'questionnaire-question-added': {
      link: `/questionnaires/${resourceId}/answer`,
      label: t('actionLabels.answer'),
    },
    'questionnaire-invitation': {
      link: `/questionnaires/${resourceId}/answer?requestId=${resourceId}`,
      label: t('actionLabels.answer'),
    },
    'questionnaire-commented': {
      link: `/questionnaires/${resourceId}/edit?subTab=reviewPerCompany&tab=responses&company=${createdByCompanyId}&count=50&page=0&filter={}`,
      label: t('actionLabels.viewComment'),
    },
    'questionnaire-response-submitted': {
      link: `/questionnaires/${resourceId}/edit?subTab=reviewPerCompany&tab=responses&company=${createdByCompanyId}&count=50&page=0&filter={}`,
      label: t('actionLabels.viewResponse'),
    },
    'questionnaire-reminder': {
      link: `/questionnaires/${resourceId}/answer`,
      label: t('actionLabels.continue'),
    },
    'questionnaire-unarchived': {
      link: `/questionnaires/${resourceId}/answer`,
      label: t('actionLabels.continue'),
    },
    'questionnaire-due-date-changed': {
      link: `/questionnaires/${resourceId}/answer`,
      label: t('actionLabels.answer'),
    },
    'questionnaire-changes-requested': {
      link: `/questionnaires/${resourceId}/answer`,
      label: t('actionLabels.continue'),
    },
    'own-questionnaire-due-date-closing': {
      link: `/questionnaires/${resourceId}/edit?tab=responses&count=50`,
      label: t('actionLabels.goToQuestionnaire'),
    },
    'request-questionnaire-due-date-closing': {
      link: `/questionnaires/${resourceId}/answer`,
      label: t('actionLabels.respond'),
    },
    'questionnaire-exported': {
      link: String(p?.downloadFileUrl ?? ''),
      label: t('actionLabels.download'),
    },
    'fiber-transaction-created': {
      link: `${OLD}/fiber/transactions/incoming/receive/${resourceId}/view`,
      label: t('actionLabels.receive'),
    },
    'supplier-network-sharing-request-created': {
      link: `${OLD}/companies/supplier-network/${createdByCompanyId}/respond`,
      label: t('actionLabels.respond'),
    },
    'supplier-network-sharing-response-created': {
      link: `${OLD}/companies/${createdByCompanyId}/view?tab=others-network-list`,
      label: t('actionLabels.connect'),
    },
    'supply-chain-collaboration-requested': {
      link: `${OLD}/company-chains/${resourceId}/graph`,
      label: t('actionLabels.respond'),
    },
    'supply-chain-collaboration-request-reminded': {
      link: `${OLD}/company-chains/${resourceId}/graph`,
      label: t('actionLabels.respond'),
    },
    'reports-tracing-operations-export': {
      link: String(p?.downloadFileUrl ?? ''),
      label: t('actionLabels.download'),
    },
    'reports-certificates-compliance-export': {
      link: String(p?.downloadFileUrl ?? ''),
      label: t('actionLabels.download'),
    },
    'slcp-added': {
      link: `${OLD}/companies/${createdByCompanyId}/view`,
      label: t('actionLabels.updateProfile'),
    },
    'slcp-assessment-access-requested': {
      link: `/slcp/assessments/${resourceId}`,
      label: t('actionLabels.reviewRequest'),
    },
    'slcp-assessment-shared': {
      link: `/slcp/assessments/${resourceId}`,
      label: t('actionLabels.viewAssessment'),
    },
    'slcp-assessment-missing-access-request': {
      link: 'https://help.retraced.com/en/collections/15228461-slcp',
      label: t('actionLabels.learnMore'),
    },
    'entity-scan-new-match': {
      link: '/entity-screening',
      label: t('actionLabels.reviewNow'),
    },
    'entity-screening-kharon-match': {
      link: '/entity-screening',
      label: t('actionLabels.reviewNow'),
    },
    'entity-screening-uflpa-match': {
      link: '/entity-screening',
      label: t('actionLabels.reviewNow'),
    },
    'styles-export-completed': {
      link: String(p?.fileUrl ?? ''),
      label: t('actionLabels.download'),
    },
    'styles-export-failed': {
      link: '/my-styles',
      label: t('actionLabels.tryAgain'),
    },
    'styles-bulk-import-completed': {
      link: '/my-styles',
      label: t('actionLabels.viewStyles'),
    },
    'capa-user-assignee-added': {
      link: `/capa/actions/${p?.capaId}`,
      label: t('actionLabels.view'),
    },
    'capa-collaborator-added': {
      link: `/capa/actions/${p?.capaId}`,
      label: t('actionLabels.view'),
    },
    'capa-responsible-company-added': {
      link: `/capa/plans/${p?.capaId}`,
      label: t('actionLabels.view'),
    },
    'capa-overdue': {
      link: `/capa/actions/${p?.capaId}`,
      label: t('actionLabels.viewCapa'),
    },
    'capa-status-update-in-review': {
      link: `/capa/actions/${p?.capaId}`,
      label: t('actionLabels.reviewCapa'),
    },
    'capa-status-update-in-review-general': {
      link: `/capa/actions/${p?.capaId}`,
      label: t('actionLabels.viewCapa'),
    },
    'capa-status-update-changes-requested': {
      link: `/capa/actions/${p?.capaId}`,
      label: t('actionLabels.reviewCapa'),
    },
    'capa-status-update-changes-requested-general': {
      link: `/capa/actions/${p?.capaId}`,
      label: t('actionLabels.viewCapa'),
    },
    'capa-status-update-done-general': {
      link: `/capa/actions/${p?.capaId}`,
      label: t('actionLabels.viewCapa'),
    },
    'capa-remediation-added': {
      link: `/capa/actions/${p?.capaId}`,
      label: t('actionLabels.viewCapa'),
    },
    'capa-remediation-removed': {
      link: `/capa/actions/${p?.capaId}`,
      label: t('actionLabels.viewCapa'),
    },
    'capa-comment-added': {
      link: `/capa/actions/${p?.capaId}`,
      label: t('actionLabels.viewCapa'),
    },
    'cap-user-assignee-added': {
      link: `/capa/plans/${p?.capId}`,
      label: t('actionLabels.view'),
    },
    'cap-collaborator-added': {
      link: `/capa/plans/${p?.capId}`,
      label: t('actionLabels.view'),
    },
    'cap-responsible-company-added': {
      link: `/capa/plans/${p?.capId}`,
      label: t('actionLabels.view'),
    },
    'cap-overdue': {
      link: `/capa/plans/${p?.capId}`,
      label: t('actionLabels.viewCap'),
    },
    'cap-comment-added': {
      link: `/capa/plans/${p?.capId}`,
      label: t('actionLabels.viewCap'),
    },
  };

  return map[trigger] ?? null;
}

export function getDescriptionSegments(
  n: Notification,
  t: (key: string, params?: Record<string, string | number>) => string,
): DescSegment[] {
  const {
    notificationTriggerId: trigger,
    createdByCompanyName: co,
    resourceName: res,
    payload: p,
  } = n;

  function b(prefix: string, boldText: string, suffix = ''): DescSegment[] {
    return [
      { text: prefix ? `${prefix} ` : '', bold: false },
      { text: boldText, bold: true },
      { text: suffix ? ` ${suffix}` : '', bold: false },
    ];
  }

  const map: Record<string, DescSegment[]> = {
    'own-certificates-expiring': b(
      t('triggers.ownCertificatesExpiring.prefix'),
      res,
      t('triggers.ownCertificatesExpiring.suffix'),
    ),
    'own-certificates-expired': b(
      t('triggers.ownCertificatesExpired.prefix'),
      res,
      t('triggers.ownCertificatesExpired.suffix'),
    ),
    'claim-rejected': b(
      t('triggers.claimRejected.prefix'),
      res,
      t('triggers.claimRejected.suffix'),
    ),
    'audit-requested': b(t('triggers.auditRequested.prefix', { co }), res),
    'document-requested': b(
      t('triggers.documentRequested.prefix', { co }),
      res,
    ),
    'assessment-requested': b(
      t('triggers.assessmentRequested.prefix', { co }),
      res,
    ),
    'assessment-commented': b(
      t('triggers.assessmentCommented.prefix', { co }),
      res,
    ),
    'assessment-requested-company-notify': b(
      t('triggers.assessmentRequestedCompanyNotify.prefix', { co }),
      res,
    ),
    'assessment-question-answered': b(
      t('triggers.assessmentQuestionAnswered.prefix', { co }),
      res,
    ),
    'assessment-unarchived': b(
      t('triggers.assessmentUnarchived.prefix', { co }),
      res,
    ),
    'questionnaire-question-added': b(
      t('triggers.questionnaireQuestionAdded.prefix', { co }),
      res,
    ),
    'questionnaire-invitation': b(
      t('triggers.questionnaireInvitation.prefix', { co }),
      res,
    ),
    'questionnaire-commented': b(
      t('triggers.questionnaireCommented.prefix', {
        co: String(p?.commenterUserName ?? co),
      }),
      res,
    ),
    'questionnaire-response-submitted': b(
      t('triggers.questionnaireResponseSubmitted.prefix', { co }),
      res,
    ),
    'questionnaire-reminder': b(
      t('triggers.questionnaireReminder.prefix'),
      res,
    ),
    'questionnaire-unarchived': b(
      t('triggers.questionnaireUnarchived.prefix', { co }),
      res,
    ),
    'questionnaire-due-date-changed': b(
      t('triggers.questionnaireDueDateChanged.prefix'),
      res,
    ),
    'questionnaire-changes-requested': b(
      t('triggers.questionnaireChangesRequested.prefix', { co }),
      res,
    ),
    'own-questionnaire-due-date-closing': b(
      t('triggers.ownQuestionnaireDueDateClosing.prefix'),
      res,
      t('triggers.ownQuestionnaireDueDateClosing.suffix'),
    ),
    'request-questionnaire-due-date-closing': b(
      t('triggers.requestQuestionnaireDueDateClosing.prefix', { co }),
      res,
      t('triggers.requestQuestionnaireDueDateClosing.suffix'),
    ),
    'fiber-transaction-created': b(
      t('triggers.fiberTransactionCreated.prefix', { co }),
      res,
    ),
    'supplier-network-sharing-request-created': [
      {
        text: t('triggers.supplierNetworkSharingRequestCreated', { co }),
        bold: false,
      },
    ],
    'supplier-network-sharing-response-created': [
      {
        text: t('triggers.supplierNetworkSharingResponseCreated', {
          co,
          count: String(p?.noProfiles ?? ''),
        }),
        bold: false,
      },
    ],
    'supply-chain-collaboration-requested': b(
      t('triggers.supplyChainCollaborationRequested.prefix', { co }),
      res,
    ),
    'supply-chain-collaboration-request-reminded': b(
      t('triggers.supplyChainCollaborationRequestReminded.prefix', { co }),
      res,
    ),
    'reports-tracing-operations-export': [
      { text: t('triggers.reportTracingOperationsExport'), bold: false },
    ],
    'reports-certificates-compliance-export': [
      { text: t('triggers.reportCertificatesComplianceExport'), bold: false },
    ],
    'styles-export-completed': [
      {
        text: t('triggers.stylesExportCompleted', {
          count: String(p?.totalRows ?? 0),
        }),
        bold: false,
      },
    ],
    'styles-export-failed': [
      { text: t('triggers.stylesExportFailed'), bold: false },
    ],
    'styles-bulk-import-completed': [
      {
        text: String(p?.message ?? t('triggers.stylesBulkImportCompleted')),
        bold: false,
      },
    ],
    'entity-scan-new-match': [
      {
        text: t('triggers.entityScanNewMatch', {
          count: String(p?.newMatchesCount ?? 1),
          entity:
            (p?.newMatchesCount as number) === 1
              ? t('triggers.entityScanNewMatchSingular')
              : t('triggers.entityScanNewMatchPlural'),
        }),
        bold: false,
      },
    ],
    'entity-screening-kharon-match': [
      { text: t('triggers.entityScreeningKharonMatch'), bold: false },
    ],
    'entity-screening-uflpa-match': [
      { text: t('triggers.entityScreeningUflpaMatch'), bold: false },
    ],
    'capa-user-assignee-added': b(
      t('triggers.capaUserAssigneeAdded.prefix', {
        co: String(p?.assignorCompanyName ?? co),
      }),
      String(p?.capaTitle ?? ''),
    ),
    'capa-collaborator-added': b(
      t('triggers.capaCollaboratorAdded.prefix', {
        co: String(p?.assignorCompanyName ?? co),
      }),
      String(p?.capaTitle ?? ''),
    ),
    'capa-responsible-company-added': b(
      t('triggers.capaResponsibleCompanyAdded.prefix', {
        co: String(p?.assignorCompanyName ?? co),
      }),
      String(p?.capaTitle ?? ''),
    ),
    'capa-overdue': b(
      t('triggers.capaOverdue.prefix'),
      String(p?.capaTitle ?? ''),
      t('triggers.capaOverdue.suffix'),
    ),
    'capa-status-update-in-review': b(
      t('triggers.capaStatusUpdateInReview.prefix', {
        co: String(p?.updaterCompanyName ?? co),
      }),
      String(p?.capaTitle ?? ''),
      t('triggers.capaStatusUpdateInReview.suffix'),
    ),
    'capa-status-update-in-review-general': b(
      t('triggers.capaStatusUpdateInReview.prefix', {
        co: String(p?.updaterCompanyName ?? co),
      }),
      String(p?.capaTitle ?? ''),
      t('triggers.capaStatusUpdateInReview.suffix'),
    ),
    'capa-status-update-changes-requested': b(
      t('triggers.capaStatusUpdateChangesRequested.prefix', {
        co: String(p?.updaterCompanyName ?? co),
      }),
      String(p?.capaTitle ?? ''),
    ),
    'capa-status-update-changes-requested-general': b(
      t('triggers.capaStatusUpdateChangesRequested.prefix', {
        co: String(p?.updaterCompanyName ?? co),
      }),
      String(p?.capaTitle ?? ''),
    ),
    'capa-status-update-done-general': b(
      t('triggers.capaStatusUpdateDoneGeneral.prefix', {
        co: String(p?.updaterCompanyName ?? co),
      }),
      String(p?.capaTitle ?? ''),
      t('triggers.capaStatusUpdateDoneGeneral.suffix'),
    ),
    'capa-remediation-added': b(
      t('triggers.capaRemediationAdded.prefix', {
        co: String(p?.adderCompanyName ?? co),
      }),
      String(p?.capaTitle ?? ''),
    ),
    'capa-remediation-removed': b(
      t('triggers.capaRemediationRemoved.prefix', {
        co: String(p?.removerCompanyName ?? co),
      }),
      String(p?.capaTitle ?? ''),
    ),
    'capa-comment-added': b(
      t('triggers.capaCommentAdded.prefix', {
        co: String(p?.commentorCompanyName ?? co),
      }),
      String(p?.capaTitle ?? ''),
    ),
    'cap-user-assignee-added': b(
      t('triggers.capUserAssigneeAdded.prefix', {
        co: String(p?.assignorCompanyName ?? co),
      }),
      String(p?.capTitle ?? ''),
    ),
    'cap-collaborator-added': b(
      t('triggers.capCollaboratorAdded.prefix', {
        co: String(p?.assignorCompanyName ?? co),
      }),
      String(p?.capTitle ?? ''),
    ),
    'cap-responsible-company-added': b(
      t('triggers.capResponsibleCompanyAdded.prefix', {
        co: String(p?.assignorCompanyName ?? co),
      }),
      String(p?.capTitle ?? ''),
    ),
    'cap-overdue': b(
      t('triggers.capOverdue.prefix'),
      String(p?.capTitle ?? ''),
      t('triggers.capOverdue.suffix'),
    ),
    'cap-comment-added': b(
      t('triggers.capCommentAdded.prefix', {
        co: String(p?.commentorCompanyName ?? co),
      }),
      String(p?.capTitle ?? ''),
    ),
  };

  return (
    map[trigger] ?? [
      { text: `${co} — ${trigger.replace(/-/g, ' ')}`, bold: false },
    ]
  );
}
